import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { INACTIVITY_TIMEOUT, WARNING_TIMEOUT } from "../utils/constants";
import { useActions } from "../services/hooks/useActions";
import { useAppSelector } from "../services/hooks";
import { isAuthSelector } from "../services/selectors/auth.selectors";
import { isStayAwakeActive, clearStayAwakeMode, STAY_AWAKE_CHANNEL } from "../utils/stayAwakeMode";

const useInactivityTimer = (
  setShowWarning: Dispatch<SetStateAction<boolean>>,
  setShowLogoutModal: Dispatch<SetStateAction<boolean>>
) => {
  const navigate = useNavigate();
  const { logout } = useActions();
  const isAuth = useAppSelector(isAuthSelector);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const warningTimerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!isAuth) return;

    // Create broadcast channel for cross-tab communication
    channelRef.current = new BroadcastChannel('user_activity');

    const resetTimers = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      setShowWarning(false);

      warningTimerRef.current = setTimeout(() => {
        // Don't show warning while "Stay Awake" mode is active
        if (isStayAwakeActive()) {
          resetTimers();
          return;
        }
        setShowWarning(true);
        // Notify other tabs that warning is shown
        channelRef.current?.postMessage({ type: 'warning_shown' });
      }, WARNING_TIMEOUT);

      timerRef.current = setTimeout(() => {
        // Don't logout while "Stay Awake" mode is active
        if (isStayAwakeActive()) {
          resetTimers();
          return;
        }
        logout();
        setShowWarning(false);
        setShowLogoutModal(true);
        // Notify other tabs about logout
        channelRef.current?.postMessage({ type: 'logged_out' });
        navigate("/");
      }, INACTIVITY_TIMEOUT);
    };

    const handleActivity = () => {
      resetTimers();
      // Notify other tabs about activity
      channelRef.current?.postMessage({ type: 'activity' });
    };

    // Listen for messages from other tabs
    channelRef.current.onmessage = (event) => {
      switch (event.data.type) {
        case 'activity':
          // Activity in another tab - reset timers in this tab too
          resetTimers();
          break;
        case 'warning_shown':
          // Warning shown in another tab - show here too
          setShowWarning(true);
          break;
        case 'warning_dismissed':
          // Warning was dismissed in another tab - dismiss here too
          setShowWarning(false);
          resetTimers();
          break;
        case 'logged_out':
          // User logged out in another tab - logout here too
          clearStayAwakeMode();
          logout();
          setShowWarning(false);
          setShowLogoutModal(true);
          navigate("/");
          break;
      }
    };

    // Listen for stay-awake mode changes — when toggled, reset timers
    // so pending warning/logout timeouts are rescheduled.
    const stayAwakeChannel = new BroadcastChannel(STAY_AWAKE_CHANNEL);
    stayAwakeChannel.onmessage = (event: MessageEvent) => {
      if (event.data?.type === 'stay_awake_changed') {
        resetTimers();
      }
    };

    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity);
    window.addEventListener("click", handleActivity);

    resetTimers(); // Initialize timers

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity);
      window.removeEventListener("click", handleActivity);
      channelRef.current?.close();
      stayAwakeChannel.close();
    };
  }, [logout, navigate, setShowWarning, setShowLogoutModal, isAuth]);
};

export default useInactivityTimer;
